import {
  AttachMoney,
  CalendarMonth,
  Login,
  ShoppingCart,
  Warning,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { format, parseISO } from "date-fns";
import { groupBy, map, reduce } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CountUp } from "../../../components/CountUp";
import { Paper } from "../../../components/Paper";
import { PaperChart } from "../../../components/PaperChart";
import { Table } from "../../../components/Table";
import { useAuth } from "../../../hooks/useAuth";
import { CEP, CNPJ, CPF } from "../../../lib/masks";
import { listCampaignsPerson, listSalesPerson } from "../../../services/person";
import { Campaign, Person, TimelineSales } from "../../../types/person";
import { useStyles } from "../styles";

type OverviewProps = {
  person: Person;
  loading?: boolean;
};

export function Overview({ person, loading }: OverviewProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const styles = useStyles();
  const params = useParams();

  const campaigns = useQuery(["campaigns", params?.id], () =>
    listCampaignsPerson(Number(params?.id))
  );

  const sales = useQuery(["sales", params?.id], () =>
    listSalesPerson(Number(params?.id))
  );

  const textAlert = useMemo(() => {
    const cnpjFormatted = CNPJ(user.companyDocument);
    return `Seu uso deve ser exclusivo da empresa ${user.companyName}, inscrita no
        CNPJ sob o nº ${cnpjFormatted}, e apenas para auxílio no processo
        de relacionamento. A divulgação de quaisquer informações à terceiros
        poderá ser punida de acordo com o previsto por lei.`;
  }, [user]);

  const textAddress = useMemo(() => {
    return person
      ? `${person.endereco}
        ${Boolean(person.bairro) ? `, ${person.bairro}` : ""}
        ${Boolean(person.cidade) ? `, ${person.cidade}` : ""}
        ${Boolean(person.uf) ? `, ${person.uf}` : ""}
        ${Boolean(person.cep) ? ` - ${CEP(person.cep)}` : ""}
        ${Boolean(person.numero) ? `, ${person.numero}` : ""}`
      : "";
  }, [person]);

  const purchases = useMemo(() => {
    const groupPurchases = groupBy(sales.data?.timeline, (data) => data.cupom);
    const purchase: any[] = map(Object.keys(groupPurchases), (coupon) => {
      const saleTotal = map(groupPurchases[coupon], (cupom) =>
        Number(cupom.total)
      );

      const objectPurchaseData = {
        coupon: coupon,
        data_venda: groupPurchases[coupon][0]?.data_venda,
        total: reduce(
          saleTotal,
          (prevValue, currValue) => prevValue + currValue,
          0
        ),
        products: groupPurchases[coupon],
      };
      return objectPurchaseData;
    });
    return purchase.sort((a, b) => {
      const dateA = new Date(a.data_venda).getTime();
      const dateB = new Date(b.data_venda).getTime();
      return dateB - dateA;
    });
  }, [sales.data?.timeline]);

  const RenderValue = useCallback((item: TimelineSales) => {
    return (
      <CountUp start={0} prefix="R$" decimals={2} end={Number(item?.total)} />
    );
  }, []);

  const RenderDetailCampaign = useCallback(
    (item: Campaign) => {
      return (
        <Tooltip title="Visualizar campanha" placement="top">
          <IconButton
            onClick={() =>
              navigate(`/results/${item.id_campanha_lista}`, {
                replace: true,
                state: {
                  name: item.nome_campanha,
                },
              })
            }
          >
            <Login />
          </IconButton>
        </Tooltip>
      );
    },
    [navigate]
  );

  const RenderDetailPurchase = useCallback(
    (data: any) => {
      return (
        <Table
          toolbar={false}
          data={data?.products as []}
          style={{
            backgroundColor: "#FFF",
            border: "1px solid rgba(0, 0, 0, 0.12)",
          }}
          headerStyle={{ backgroundColor: "transparent" }}
          columns={[
            { title: "Produto", field: "nome_produto" },
            { title: "Característica 1", field: "desc_categoria_2" },
            { title: "Característica 2", field: "desc_categoria_3" },
            { title: "Característica 3", field: "desc_categoria_4" },
            {
              title: "Valor",
              field: "total",
              render: RenderValue,
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 5,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
        />
      );
    },
    [RenderValue]
  );

  return (
    <Stack spacing={4}>
      <Alert variant="filled" icon={<Warning />} severity="warning">
        <Typography variant="body1" fontWeight="bold" marginBottom={1}>
          ESTA É UMA INFORMAÇÃO CONFIDENCIAL
        </Typography>
        <Typography variant="subtitle2">{textAlert}</Typography>
      </Alert>
      <Stack direction="row" width="100%" spacing={2}>
        <Paper
          icon={<AttachMoney color="primary" fontSize="large" />}
          label="Valor em compras"
          className={styles.paper}
          variant="outlined"
          elevation={0}
          decimals={2}
          prefix="R$"
          start={0}
          loading={loading}
          end={Number(person?.valor_total_gasto || 0)}
        />

        <Paper
          icon={<ShoppingCart color="primary" fontSize="large" />}
          label="Quantidade de compras"
          className={styles.paper}
          variant="outlined"
          elevation={0}
          start={0}
          loading={loading}
          end={Number(person?.qt_compras || 0)}
        />
        <PaperChart
          loading={loading}
          className={styles.paper}
          variant="outlined"
          elevation={0}
        >
          <Stack direction="row" spacing={4} alignItems="center">
            <CalendarMonth color="primary" fontSize="large" />
            <Stack spacing={1} alignItems="center">
              {person?.data_ultima_compra ? (
                <Typography fontSize={25} fontWeight={600} color="primary">
                  {format(
                    parseISO(person.data_ultima_compra?.replace("Z", "")),
                    "dd/MM/yyyy HH:mm"
                  )}
                </Typography>
              ) : (
                <Typography fontSize={25} fontWeight={600} color="primary">
                  Data não encontrada
                </Typography>
              )}

              <Typography variant="subtitle1" color="GrayText">
                Data da última compra
              </Typography>
            </Stack>
          </Stack>
        </PaperChart>
      </Stack>
      <PaperChart
        label="Dados cadastrais"
        className={styles.paperWithoutWidth}
        variant="outlined"
        elevation={0}
      >
        <Stack spacing={2}>
          <Typography>
            <b>ID:</b> {person ? person?.id_pessoa : <Skeleton />}
          </Typography>
          <Typography>
            <b>Nome: </b> {person ? person?.nome : <Skeleton />}
          </Typography>
          <Typography>
            <b>Documento: </b> {person ? CPF(person?.documento) : <Skeleton />}
          </Typography>
          {person?.data_nascimento && (
            <Typography>
              <b>Data de nascimento: </b>
              {format(
                parseISO(person?.data_nascimento.replace("Z", "")),
                "dd/MM/yyyy"
              )}
            </Typography>
          )}
          <Typography>
            <b>Email: </b> {person ? person?.email1 : <Skeleton />}
          </Typography>
          <Typography>
            <b>Endereço: </b> {person ? textAddress : <Skeleton />}
          </Typography>
          {person?.data_insert && (
            <Typography>
              <b>Data de cadastro: </b>
              {format(
                parseISO(person?.data_insert.replace("Z", "")),
                "dd/MM/yyyy"
              )}
            </Typography>
          )}
          <Typography>
            <Stack direction="row" spacing={1}>
              <b>Ticket médio:</b>
              <CountUp
                start={0}
                prefix="R$"
                decimals={2}
                end={Number(person?.ticket_medio)}
              />
            </Stack>
          </Typography>
          <Typography>
            <Stack direction="row" spacing={1}>
              <b>Percentual de desconto:</b>
              <CountUp
                start={0}
                suffix="%"
                decimals={3}
                end={Number(person?.percentual_desconto)}
              />
            </Stack>
          </Typography>
        </Stack>
      </PaperChart>

      <PaperChart
        label="Compras realizadas"
        className={styles.paperWithoutWidth}
        variant="outlined"
        elevation={0}
      >
        <Table
          toolbar={false}
          data={purchases as []}
          isLoading={sales.isFetching}
          style={{ backgroundColor: "transparent" }}
          headerStyle={{ backgroundColor: "transparent" }}
          onRowClick={(event, rowData, togglePanel) => togglePanel()}
          columns={[
            { title: "Cupom", field: "coupon" },
            {
              title: "Data de compra",
              field: "data_venda",
              render: (row) =>
                format(
                  parseISO(row.data_venda?.replace("Z", "")),
                  "dd/MM/yyyy HH:mm"
                ),
            },
            {
              title: "Total",
              field: "total",
              render: RenderValue,
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 5,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
          detailPanel={[
            {
              icon: "shopping_cart_outlined",
              openIcon: "shopping_cart",
              render: RenderDetailPurchase,
            },
          ]}
        />
      </PaperChart>

      <PaperChart
        label="Campanhas"
        className={styles.paperWithoutWidth}
        variant="outlined"
        elevation={0}
      >
        <Table
          toolbar={false}
          data={campaigns.data as []}
          isLoading={campaigns.isFetching}
          columns={[
            { title: "Campanha", field: "nome_campanha" },
            {
              title: "Data do disparo",
              field: "data_agendamento",
              render: (row) =>
                format(parseISO(row?.data_agendamento), "dd/MM/yyyy HH:mm"),
            },
            {
              title: "Ações",
              field: "data_agendamento",
              render: RenderDetailCampaign,
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 5,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
          style={{ backgroundColor: "transparent" }}
          headerStyle={{ backgroundColor: "transparent" }}
        />
      </PaperChart>
    </Stack>
  );
}
